
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import AttachmentsList from '@/components/AttachmentsList.vue';
import AttachmentsGallery from '@/components/AttachmentsGallery.vue';
import QuotationsList from '@/components/QuotationsList.vue';
import TaskStatusAlert from '@/components/TaskStatusAlert.vue';
import TaskActions from '@/components/TaskActions.vue';
import TaskPFollowUpDialog from '@/components/TaskPFollowUpDialog.vue';
import SelectNetworkDialog from '@/components/SelectNetworkDialog.vue';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import {
  Attachment, AttachmentType,
  Lead, LeadStatus, LeadType, Network, PermissionCode,
  ProjectsPool,
  Quotation, QuotationProperty, Task, TaskStatus,
} from '@/types';
import {
  currentUserHasPermission, getLeadCustomValueDate,
  getLeadCustomValueText, getLeadCustomValue,
} from '@/helpers';

moment.locale('it');

@Component({
  components: {
    AttachmentsList,
    AttachmentsGallery,
    QuotationsList,
    TaskStatusAlert,
    TaskActions,
    TaskPFollowUpDialog,
    SelectNetworkDialog,
    DatePickerDialog,
  },
})
export default class TaskP extends Vue {
  @Prop(Object) readonly task: Task | undefined;
  @Prop({ type: Boolean, default: false }) readonly embedded!: boolean;
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;

  // Il form è valido?
  isFormValid = false

  selectedStatus: 'completed' | 'error' = 'completed'

  networkDialog = false
  followUpDialog = false
  galleryDialog = false

  datePickerDialog = false
  datePickerDate: string | null = null
  datePickerName: string | null = null

  step3Descr: string | null = null
  step3Quote: string | null = null
  step3Notes: string | null = null
  noteFollowUp: string | null = null
  dataFollowUp: Date | null = null

  created() {
    console.log(`${this.constructor.name}: created()`); // eslint-disable-line no-console

    // Inizializza tutti i campi del modulo
    this.refreshTask();
  }

  // Aggiorna i campi con gli eventuali nuovi valori della Lead selezionata
  refreshTask() {
    console.log(`${this.constructor.name}: refreshTask()`); // eslint-disable-line no-console

    this.step3Descr = this.selectedLead?.step3Descr ?? '';
    this.step3Quote = this.selectedLead?.step3Quote ?? '';
    this.step3Notes = this.selectedLead?.step3Notes ?? '';
    this.noteFollowUp = getLeadCustomValueText(this.selectedLead, 'noteFollowUp');
    this.dataFollowUp = getLeadCustomValueDate(this.selectedLead, 'dataFollowUp');
  }

  // Il Task è valido quando sia il form che i dati extra lo sono
  get valid(): boolean {
    return this.isFormValid && this.isExtraDataValid;
  }

  // I dati extra sono validi?
  get isExtraDataValid(): boolean {
    // Se è impossibile completare il task, i dati extra sono ignorati
    // FIXME: Rimuovere quando questo Task userà il mixin TaskMixin
    if (this.selectedStatus === 'error') return true;

    // Manca il preventivo
    if (!this.hasAttachments('preventivo') && this.selectedStatus === 'completed') return false;

    // In tutti gli altri casi...
    return true;
  }

  // Se la validità del form cambia, la memorizza nello stato di Vuex
  @Watch('valid')
  onValidChange(val: boolean) {
    console.log(`${this.constructor.name}: onValidChange() val =`, val); // eslint-disable-line no-console

    this.$store.dispatch('updateTaskFieldsValidity', { value: val });
  }

  get selectedLead(): Lead | null {
    const childLeads: ProjectsPool = this.$store.getters.currentChildren;

    // Se è stato specificato un Task e questo è associato ad una Lead figlia...
    if (this.task && this.task.relationship === 'father') {
      // ... se la lead figlia non è stata ancora assegnata,
      // non ritorna nulla.
      if ('childLeadId' in this.task === false || !this.task.childLeadId) return null;

      // ... se la lead figlia è stata assegnata,
      // considera i suoi dati.
      return childLeads[this.task.childLeadId];
    }

    // Negli altri casi ritorna la Lead selezionata memorizzata tramite Vuex
    return this.$store.getters.currentLead;
  }

  get selectedTask(): Task | null {
    // Se non è stato specificato un Task nelle Prop,
    // utilizza il Task selezionato memorizzato tramite Vuex.
    return this.task ?? this.$store.getters.selectedTask;
  }

  get loading(): boolean {
    // Se è stato specificato un Task nelle Prop,
    // non utilizza questo boolean (sempre false).
    return this.task
      ? false
      : this.$store.getters.isTaskLoading;
  }

  get updating(): boolean {
    // Se è stato specificato un Task nelle Prop,
    // non utilizza questo boolean (sempre false).
    return this.task
      ? false
      : this.$store.getters.isTaskUpdating;
  }

  get leadType(): LeadType | null { return this.selectedLead?.type ?? null; }

  // Ritorma true se è in corso l'aggiornamento della Lead mostrata
  get leadUpdating(): boolean { return this.$store.getters.isProjectUpdating; }

  // Tiene sotto controllo leadUpdateing per capire quando eventualmente
  // vanno aggiornati i dati visualizzati.
  @Watch('leadUpdating')
  onLeadUpdatingChange(val: boolean, oldVal: boolean) {
    console.log(`${this.constructor.name}: onLeadUpdatingChange() val = ${val} oldVal = ${oldVal}`); // eslint-disable-line no-console

    // Se leadUpdating passa da true a folse,
    // la Lead ha completato l'aggiornamento e vanno aggiornati i dati visualizzati.
    if (val === false && oldVal === true) {
      // Aggiorna la Lead con i nuovi valori soltanto se è in visualizzazione readonly
      if (this.readonly) this.refreshTask();
    }
  }

  get leadStatus(): LeadStatus | null { return this.selectedLead?.status ?? null; }

  get taskStatus(): TaskStatus { return this.selectedTask?.status ?? null; }

  get networks(): Network[] {
    return this.$store.getters.networks;
  }

  get quotations() {
    return this.selectedLead
      && Array.isArray(this.selectedLead.Quotations)
      && this.selectedLead.Quotations.length > 0
      ? this.selectedLead.Quotations
      : [];
  }

  get hasQuotations() {
    return this.quotations.length > 0;
  }

  get visibleQuotations() {
    return this.quotations.filter((q: Quotation, index: number) => this.taskStatus !== 'completed' || q.complete || index === 0);
  }

  get form(): Vue & { validate: () => boolean; reset: () => boolean } {
    return this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };
  }

  get attachments(): Attachment[] { return this.selectedLead?.Attachments ?? []; }

  get followUpVisible(): boolean {
    return this.noteFollowUp !== null || this.dataFollowUp !== null;
  }

  // Configurazione Eturnity
  get hasEturnityConfig(): boolean {
    // Integrazione spenta: il riquadro non compare, nemmeno su una lead che
    // conservasse i dati di una vecchia configurazione.
    if (!this.$store.getters.getIntegrations.eturnity.enabled) return false;
    return this.latestEturnityConfig !== null;
  }

  get latestEturnityConfig(): any {
    if (!this.selectedLead?.CustomValues) return null;

    // Cerca configurazioni Eturnity (formato unificato eturnityConfig_{configId}_status)
    const eturnityConfigs = this.selectedLead.CustomValues
      .filter((cv) => cv.name.match(/^eturnityConfig_(\d+_\d+)_status$/))
      .filter((cv) => cv.value === 'completed')
      .map((cv) => {
        const match = cv.name.match(/^eturnityConfig_(\d+_\d+)_status$/);
        if (!match) return null;
        const configId = match[1];
        return {
          configId,
          status: cv.value,
          data: getLeadCustomValueText(this.selectedLead, `eturnityConfig_${configId}_data`),
          pdfReference: getLeadCustomValueText(this.selectedLead, `eturnityConfig_${configId}_pdfReference`),
          attachmentId: getLeadCustomValueText(this.selectedLead, `eturnityConfig_${configId}_attachmentId`),
          completedAt: getLeadCustomValueDate(this.selectedLead, `eturnityConfig_${configId}_completedAt`),
        };
      })
      .filter((config): config is NonNullable<typeof config> => config !== null);

    // Restituisce la configurazione più recente
    if (eturnityConfigs.length > 0) {
      return eturnityConfigs.sort((a, b) => {
        if (!a.completedAt || !b.completedAt) return 0;
        return new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime();
      })[0];
    }

    return null;
  }

  get eturnityConfigDescription(): string {
    const config = this.latestEturnityConfig;
    if (!config?.configId) {
      return '';
    }

    try {
      // Prova prima a utilizzare i dati estratti dal PDF
      const extractedData = getLeadCustomValueText(this.selectedLead, `eturnityConfig_${config.configId}_extractedData`);
      if (extractedData) {
        const parsedData = JSON.parse(extractedData);
        if (parsedData.components) {
          const components: string[] = [];

          // Pannelli solari
          if (parsedData.components.panels?.producer && parsedData.components.panels?.model) {
            // eslint-disable-next-line prefer-destructuring
            const count = parsedData.components.panels.count;
            const countText = count ? `${count} ` : '';
            components.push(`${countText}Pannelli solari ${parsedData.components.panels.producer} ${parsedData.components.panels.model}`);
          }

          // Inverter
          if (parsedData.components.inverter?.producer) {
            const inverterDesc = parsedData.components.inverter.model
              ? `Inverter ${parsedData.components.inverter.producer} ${parsedData.components.inverter.model}`
              : `Inverter ${parsedData.components.inverter.producer}`;
            components.push(inverterDesc);
          }

          // Sistema di accumulo
          if (parsedData.components.storage?.producer && parsedData.components.storage?.model) {
            components.push(`Sistema di accumulo ${parsedData.components.storage.producer} ${parsedData.components.storage.model}`);
          }

          if (components.length > 0) {
            return components.join(' + ');
          }
        }
      }

      // Fallback: utilizza i dati JSON originali se i dati estratti non sono disponibili
      if (config?.data) {
        const configData = JSON.parse(config.data);
        const variant = configData.variants?.[0];

        if (!variant?.bom || !Array.isArray(variant.bom)) {
          return '';
        }

        // Estrae gli elementi dal BOM e crea la descrizione
        const bomItems = variant.bom
          .filter((item: any) => item.item && item.quantity)
          .map((item: any) => {
            // eslint-disable-next-line prefer-destructuring
            const quantity = item.quantity;
            // eslint-disable-next-line prefer-destructuring
            const itemName = item.item;
            return `${quantity} ${itemName}`;
          });

        if (bomItems.length === 0) {
          return '';
        }

        // Unisce gli elementi con " + " tranne l'ultimo
        if (bomItems.length === 1) {
          return bomItems[0];
        }
        const lastItem = bomItems.pop();
        const description = `${bomItems.join(' + ')} + ${lastItem}`;
        return description;
      }

      return '';
    } catch (error) {
      console.error('Errore nel parsing dei dati Eturnity:', error);
      return '';
    }
  }

  get eturnityConfigValue(): string {
    const config = this.latestEturnityConfig;
    if (!config?.configId) {
      return '';
    }

    try {
      // Prova prima a utilizzare i dati estratti dal PDF
      const extractedData = getLeadCustomValueText(this.selectedLead, `eturnityConfig_${config.configId}_extractedData`);
      if (extractedData) {
        const parsedData = JSON.parse(extractedData);
        if (parsedData.configurationValue) {
          return `€ ${parsedData.configurationValue.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (IVA esclusa)`;
        }
      }

      // Fallback: utilizza i dati JSON originali se i dati estratti non sono disponibili
      if (config?.data) {
        const configData = JSON.parse(config.data);
        const variant = configData.variants?.[0];

        if (!variant?.bom_totals?.subtotal_eur) {
          return '';
        }

        const subtotal = variant.bom_totals.subtotal_eur;
        return `€ ${subtotal.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (IVA esclusa)`;
      }

      return '';
    } catch (error) {
      console.error('Errore nel parsing dei dati Eturnity per il valore:', error);
      return '';
    }
  }

  get eturnityPdfAttachment(): Attachment | null {
    if (!this.latestEturnityConfig?.attachmentId) return null;

    // Cerca l'allegato PDF tramite ID diretto (più efficiente e affidabile)
    const attachmentId = parseInt(this.latestEturnityConfig.attachmentId, 10);
    return this.attachments.find(
      (attachment) => attachment.id === attachmentId,
    ) || null;
  }

  get eturnityPdfAttachments(): Attachment[] {
    const attachment = this.eturnityPdfAttachment;
    return attachment ? [attachment] : [];
  }

  get selectedEturnityAttachment(): Attachment | null {
    return this.eturnityPdfAttachment;
  }

  get eturnityExtractedComponents(): any {
    const config = this.latestEturnityConfig;
    if (!config?.configId) {
      return null;
    }

    try {
      const extractedData = getLeadCustomValueText(this.selectedLead, `eturnityConfig_${config.configId}_extractedData`);
      if (extractedData) {
        const parsedData = JSON.parse(extractedData);
        return parsedData.components || null;
      }
      return null;
    } catch (error) {
      console.error('Errore nel parsing dei componenti estratti:', error);
      return null;
    }
  }

  get appendIcon(): string | null { return this.fieldsDisabled ? 'mdi-lock' : null; }

  get fieldsDisabled(): boolean {
    return (this.task !== null && this.task !== undefined)
      || this.selectedTask?.status !== null
      || this.readonly
      || this.loading
      || this.updating;
  }

  // Mostra le azioni nell'Alert soltanto sulle lead figlie e sui Task senza figlie
  get showAlertActions(): boolean { return this.task?.relationship !== 'father'; }

  // Mostra le azioni
  get showActions(): boolean { return !this.task && !this.readonly && !this.embedded; }

  hasPermission(codename: PermissionCode): boolean { return currentUserHasPermission(codename); }

  hasAttachments(fieldname: AttachmentType): boolean {
    return this.attachments.filter((a) => a.fieldname === fieldname).length > 0;
  }

  onFieldUpdated(field: string) {
    console.log(`${this.constructor.name}: onFieldUpdated(${field})`); // eslint-disable-line no-console

    const update = {} as { [key: string]: unknown };

    // Aggiorna sempre il campo specificato
    update[field] = this[field] ?? '';

    // Aggiorna eventuali campi collegati, eccezioni, ecc.
    switch (field) {
      default:
        break;
    }

    this.$store.dispatch('updateTaskFields', update);
  }

  onClickDate(name: string) {
    console.log(`${this.constructor.name}: onClickDate(${name})`); // eslint-disable-line no-console

    if (this.leadStatus !== 'open'
      || this.taskStatus !== null
      || this.readonly) return;

    // Imposta per quale campo verrà utilizzato il DatePicker
    this.datePickerName = name;

    // Imposta l'eventuale data precedentemente selezionata
    this.datePickerDate = (this as any)[name]
      ? moment((this as any)[name]).toISOString(true).substring(0, 10)
      : null;

    // Apre il dialog per la selezione della data
    this.datePickerDialog = true;
  }

  onClearDate(name: string) {
    console.log(`${this.constructor.name}: onClearDate(${name})`); // eslint-disable-line no-console

    if (this.leadStatus !== 'open') return;

    const update = {} as { [key: string]: unknown };

    // Cancello la data nel modulo e nel prossimo aggiornamento
    if (name in this) {
      (this as any)[name] = null;
      update[name] = null;
    }

    // Eventuali reset degli switch collegati
    switch (name) {
      default:
        break;
    }

    // Valido il modulo con le nuove informazioni inserite
    this.onValidate('completed');

    this.$store.dispatch('updateTaskFields', update);
  }

  onSelectDate(date: string | null) {
    console.log(`${this.constructor.name}: onSelectDate() date =`, date); // eslint-disable-line no-console

    if (!this.datePickerName) return;

    // Chiudo il dialog per la selezione della data
    this.datePickerDialog = false;

    const update = {} as { [key: string]: unknown };

    // Trasforma in un valore di tipo Date prima di restituirlo
    const selectedDate = date
      ? moment(date, 'YYYY-MM-DD').toDate()
      : null;

    // Inserisco la data nel modulo e nell'aggiornamento
    if (this.datePickerName && this.datePickerName in this) {
      (this as any)[this.datePickerName] = selectedDate;
      update[this.datePickerName] = (this as any)[this.datePickerName];
    }

    // Modifico eventuali campi collegati
    switch (this.datePickerName) {
      default:
        break;
    }

    // Valido il modulo con le nuove informazioni inserite
    this.onValidate('completed');

    this.$store.dispatch('updateTaskFields', update);
  }

  hasPartialData(quotation: Quotation) {
    const original = JSON.parse(quotation.original);

    return 'quotation' in original && 'partialData' in original.quotation;
  }

  originalQuotation(quotation: Quotation, attr: QuotationProperty = null) {
    const original = quotation && 'original' in quotation && quotation.original
      ? JSON.parse(quotation.original)
      : null;

    if (!attr) {
      return original && 'quotation' in original && original.quotation
        ? original.quotation
        : null;
    }

    switch (attr) {
      case 'fabbisognoAnnuo':
        return new Intl.NumberFormat('it-IT').format(original.quotation.fabbisognoAnnuo);
      case 'potenzaDaConsumi':
        return new Intl.NumberFormat(
          'it-IT',
          { maximumFractionDigits: 2, minimumFractionDigits: 2 },
        ).format(original.quotation.partialData.potenzaDaConsumi);
      case 'potenzaDaSpazioPerformance':
        return new Intl.NumberFormat(
          'it-IT',
          { maximumFractionDigits: 2, minimumFractionDigits: 2 },
        ).format(original.quotation.partialData.potenzaDaSpazio.performance);
      case 'potenzaDaSpazioTop':
        return new Intl.NumberFormat(
          'it-IT',
          { maximumFractionDigits: 2, minimumFractionDigits: 2 },
        ).format(original.quotation.partialData.potenzaDaSpazio.top);
      case 'producibilitaAnnuaPerformance':
        return new Intl.NumberFormat(
          'it-IT',
          { maximumFractionDigits: 0 },
        ).format(original.quotation.partialData.producibilitaAnnua.performance);
      case 'producibilitaAnnuaTop':
        return new Intl.NumberFormat(
          'it-IT',
          { maximumFractionDigits: 0 },
        ).format(original.quotation.partialData.producibilitaAnnua.top);
      case 'faldePerformance':
        return original.quotation.partialData.numeroPannelliInstallabili
          .map((type: any) => type.performance);
      case 'faldeTop':
        return original.quotation.partialData.numeroPannelliInstallabili
          .map((type: any) => type.top);
      default:
        return null;
    }
  }

  mDate(date: Date | null | undefined): string | null { return date ? moment(date).format('L') : null; }

  onConfiguratore() {
    console.log(`${this.constructor.name}: onConfiguratore()`); // eslint-disable-line no-console

    // Se manche l'id della lead si ferma subito
    const leadId = this.selectedLead?.id;
    if (!leadId) return;

    if (this.selectedLead?.network) {
      // Se nella lead è già impostata una rete di vendita,
      // utilizza quella.

      window.location.href = `/lead/${leadId}/quotation/new`;
    } else
    if (this.networks.length === 1) {
      // Nella lead non è impostata una rete di vendita
      // ma all'utente è assegnata una sola rete, utilizza quella
      const network = this.networks[0].shortname;

      window.location.href = `/lead/${leadId}/quotation/new?&network=${network}`;
    } else
    if (this.networks.length > 1) {
      // Nella lead non è impostata una rete di vendita
      // e all'utente sono assegnate più reti di vendita.
      // Chiede quale rete utilizzare.
      this.networkDialog = true;
    }
  }

  onNetworkSelect(network) {
    console.log(`${this.constructor.name}: onNetworkSelect()`); // eslint-disable-line no-console

    if (this.selectedLead) {
      window.location.href = `/lead/${this.selectedLead.id}/quotation/new?&network=${network}`;
    }
  }

  onFollowUp() {
    console.log(`${this.constructor.name}: onFollowUp()`); // eslint-disable-line no-console

    this.followUpDialog = true;
  }

  // Effetta la validazione del modulo
  async onValidate(status: 'completed' | 'error') {
    console.log(`${this.constructor.name}: onValidate(${status})`); // eslint-disable-line no-console

    // Sceglie se validare il modulo per un completamento o per un fallimento
    this.selectedStatus = status;

    // Avvia l'effettiva validazione
    const form = this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };

    if (form) {
      const result = form.validate();
      console.log(`${this.constructor.name}: onValidate() form.validate() =`, result); // eslint-disable-line no-console
    } else {
      console.error(`${this.constructor.name}: onValidate() form =`, form); // eslint-disable-line no-console
      console.error(`${this.constructor.name}: onValidate() this.$refs =`, this.$refs); // eslint-disable-line no-console
    }

    return Promise.resolve();
  }

  onEdit() {
    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    this.$emit('edit');
  }

  onReset() {
    console.log(`${this.constructor.name}: onReset()`); // eslint-disable-line no-console

    this.$emit('reset');
  }

  onViewEturnityPdf() {
    console.log(`${this.constructor.name}: onViewEturnityPdf()`); // eslint-disable-line no-console

    if (this.eturnityPdfAttachment) {
      // Apre la galleria degli allegati per visualizzare il PDF
      this.galleryDialog = true;
    }
  }

  onDownloadEturnityPdf() {
    console.log(`${this.constructor.name}: onDownloadEturnityPdf()`); // eslint-disable-line no-console

    if (this.eturnityPdfAttachment) {
      // Scarica il PDF
      window.location.href = `/download/${this.eturnityPdfAttachment.filename}`;
    }
  }
}
